import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["type", "children", "styles"];
var _dec, _dec2, _class, _Transition;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

// test is breaking without importing React here
import { Component } from 'react';
import { ms } from '@instructure/ui-utils';
import { testable } from '@instructure/ui-testable';
import { withStyle, Global } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { BaseTransition } from './BaseTransition';
import { allowedProps, propTypes } from './props';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
---
category: components/utilities
---
@module Transition
**/
let Transition = (_dec = withStyle(generateStyle, generateComponentTheme), _dec2 = testable(), _dec(_class = _dec2(_class = (_Transition = class Transition extends Component {
  constructor(...args) {
    super(...args);
    this.ref = null;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.handleExited = () => {
      if (typeof this.props.onExited === 'function') {
        this.props.onExited(this.props.type);
      }
    };
    this.handleEntered = () => {
      if (typeof this.props.onEntered === 'function') {
        this.props.onEntered(this.props.type);
      }
    };
    // Transition helper:
    // After emotion migration the only way to keep
    // the old BaseTransition functionality with adding and removing
    // classes was to add the `Global` helper of `emotion`
    // Todo: try to refactor or replace Transition/BaseTransition component in v9.0.0. so that it is not class based
    this.renderTransitionHelper = () => {
      const styles = this.props.styles;
      return _jsx(Global, {
        styles: styles === null || styles === void 0 ? void 0 : styles.globalStyles
      });
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);
  }
  render() {
    const _this$props3 = this.props,
      type = _this$props3.type,
      children = _this$props3.children,
      styles = _this$props3.styles,
      props = _objectWithoutProperties(_this$props3, _excluded);
    const duration = ms(styles.duration);
    return _jsxs(_Fragment, {
      children: [this.renderTransitionHelper(), _jsx(BaseTransition, {
        ...props,
        enterDelay: duration,
        exitDelay: duration,
        transitionClassName: styles.classNames.transitioning,
        exitedClassName: styles.classNames.exited,
        exitingClassName: styles.classNames.exiting,
        enteredClassName: styles.classNames.entered,
        enteringClassName: styles.classNames.entering,
        onEntered: this.handleEntered,
        onExited: this.handleExited,
        elementRef: this.handleRef,
        children: children
      })]
    });
  }
}, _Transition.displayName = "Transition", _Transition.componentId = 'Transition', _Transition.allowedProps = allowedProps, _Transition.propTypes = propTypes, _Transition.defaultProps = {
  type: 'fade',
  in: false,
  unmountOnExit: false,
  transitionOnMount: false,
  transitionEnter: true,
  transitionExit: true
}, _Transition.states = BaseTransition.states, _Transition)) || _class) || _class);
export default Transition;
export { Transition };